import { Check, FileSpreadsheet, Trash2, Upload } from "lucide-react";
import { type ChangeEvent, useEffect, useRef, useState } from "react";

import { MAX_DESTINATION_CSV_BYTES, parseDestinationCsv } from "../../lib/destination-csv";

import "./destination-csv-import.css";

interface DestinationCsvImportProps {
  itemCount: number;
  mappingKey: string;
  appliedCount?: number;
  onApply: (addresses: string[]) => void;
  onClear?: () => void;
}

export function DestinationCsvImport({ itemCount, mappingKey, appliedCount = 0, onApply, onClear }: DestinationCsvImportProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const activeRef = useRef(true);
  const mappingKeyRef = useRef(mappingKey);
  mappingKeyRef.current = mappingKey;
  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);
  const applied = itemCount > 0 && appliedCount === itemCount;

  const chooseCsv = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    const startedMappingKey = mappingKey;
    try {
      if (file.size > MAX_DESTINATION_CSV_BYTES) throw new Error("Destination CSV files must be 64 KB or smaller");
      const raw = await file.text();
      if (!activeRef.current) return;
      if (mappingKeyRef.current !== startedMappingKey) {
        throw new Error("The inscription list changed while the CSV was loading. Upload it again.");
      }
      const addresses = parseDestinationCsv(raw, itemCount);
      onApply(addresses);
      setFileName(file.name);
    } catch (caught) {
      if (activeRef.current) setError(caught instanceof Error ? caught.message : "Could not read the destination CSV");
    } finally {
      if (activeRef.current) setBusy(false);
    }
  };

  return <section className={applied ? "destination-csv-import applied" : "destination-csv-import"}>
    <div className="destination-csv-copy">
      <span><FileSpreadsheet /> DESTINATION CSV</span>
      <small>ONE BC1P ADDRESS PER INSCRIPTION · FIRST ADDRESS → INSCRIPTION 01</small>
    </div>
    <div className="destination-csv-actions">
      {applied ? <span className="destination-csv-success"><Check /> {appliedCount} APPLIED{fileName ? ` · ${fileName}` : ""}</span> : null}
      <label className={busy || itemCount < 1 ? "destination-csv-button disabled" : "destination-csv-button"}>
        <Upload /> {busy ? "READING…" : applied ? "REPLACE CSV" : "UPLOAD CSV"}
        <input type="file" accept=".csv,text/csv" disabled={busy || itemCount < 1} onChange={(event) => void chooseCsv(event)} />
      </label>
      {applied && onClear ? <button className="destination-csv-clear" type="button" aria-label="Clear imported destinations" onClick={() => { onClear(); setFileName(""); setError(""); }}><Trash2 /> CLEAR</button> : null}
    </div>
    {error ? <p className="destination-csv-error" role="alert">{error}</p> : null}
  </section>;
}
